import { array, boolean, object, string, type AnySchema } from 'yup'
import type { Catalog, Service } from '@/prisma/client'

type TranslateFunction = (key: string, params?: Record<string, any>) => string

export function catalogSchema(fields: string[], t: TranslateFunction) {
  const baseSchema = {
    name: string().required(t('errors.required')).min(3, t('errors.min', { min: 3 })),
    public: boolean().nullable(),
    images: array().of(string()).nullable(),
    description: string().nullable(),
  }
  type BaseSchemaKeys = keyof typeof baseSchema
  const partialSchema: Partial<Record<BaseSchemaKeys, AnySchema>> = {}
  fields.forEach((fieldName) => {
    if (fieldName in baseSchema) {
      const key = fieldName as BaseSchemaKeys
      partialSchema[key] = baseSchema[key]
    }
  })
  return object(partialSchema)
}

export const useCatalogStore = defineStore('catalog', () => {
  const userCatalogs = ref<Catalog[]>([])
  const catalogs = ref<Catalog[]>([])
  const services = ref<Service[]>([])
  const normalizeData = (catalog: any) => {
    return catalog
  }
  async function fetchUser() {
    try {
      userCatalogs.value = await $fetch<Catalog[] | unknown>('/api/catalog/user')
      return { data: userCatalogs.value, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: userCatalogs.value, status: 'error', error: (e as any).data || e }
    }
  }
  async function fetchServices(catalogId: number) {
    try {
      services.value = await $fetch<Service[] | unknown>('/api/catalog/service', { query: { catalogId } })
      return { data: services.value, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: services.value, status: 'error', error: (e as any).data || e }
    }
  }
  async function fetch(activityId: number) {
    try {
      catalogs.value = await $fetch<Catalog[] | unknown>('/api/catalog', { query: { activityId } })
      return { data: catalogs.value, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: catalogs.value, status: 'error', error: (e as any).data || e }
    }
  }
  async function upsert(catalog: Partial<Catalog>) {
    try {
      const result = await $fetch<Catalog | unknown>('/api/catalog', {
        method: 'POST',
        body: normalizeData(catalog),
      })
      if (result) {
        const index = catalogs.value?.findIndex(s => s.id === result.id)
        if (index !== undefined && index > -1) {
          catalogs.value?.splice(index, 1, result)
        } else {
          catalogs.value = [...(catalogs.value || []), result]
        }
      }
      return { data: result, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: null, status: 'error', error: (e as any).data || e }
    }
  }
  async function remove(id: number) {
    try {
      const result = await $fetch<Catalog | unknown>(`/api/catalog`, {
        method: 'DELETE',
        query: { id },
      })
      if (result) {
        const index = catalogs.value?.findIndex(s => s.id === id)
        if (index !== -1) {
          catalogs.value?.splice(index, 1)
        }
      }
      return { data: result, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: null, status: 'error', error: (e as any).data || e }
    }
  }
  async function updateOrder() {
    try {
      const result = await $fetch<Catalog | unknown>(`/api/catalog/order`, {
        method: 'POST',
        body: {
          catalogs: catalogs.value?.map((catalog: Catalog) => ({ id: catalog.id, order: catalog.order })),
        },
      })
      if (!result.error) {
        catalogs.value = catalogs.value?.sort((a, b) => a.order - b.order)
      }
      return { data: result, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: null, status: 'error', error: (e as any).data || e }
    }
  }
  async function updateServicesOrder(catalogId: number) {
    try {
      const result = await $fetch<Catalog | unknown>(`/api/catalog/service-order`, {
        method: 'POST',
        body: {
          services: services.value?.map((service: Service) => ({ serviceId: service.id, catalogId, order: service.order })),
        },
      })
      if (!result.error) {
        services.value = services.value?.sort((a, b) => a.order - b.order)
      }
      return { data: result, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: null, status: 'error', error: (e as any).data || e }
    }
  }
  async function addService(catalogId: number, service: Service, order: number) {
    try {
      const result = await $fetch<Catalog | unknown>('/api/catalog/service', {
        method: 'POST',
        body: { catalogId, serviceId: service.id, order },
      })
      if (!result.error) {
        services.value.push({ ...service, order })
        services.value = services.value?.sort((a, b) => a.order - b.order)
      }
      return { data: result, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: null, status: 'error', error: (e as any).data || e }
    }
  }
  async function removeService(catalogId: number, serviceId: number) {
    try {
      const result = await $fetch('/api/catalog/service', {
        method: 'DELETE',
        query: { catalogId, serviceId },
      })
      if (result) {
        const index = services.value?.findIndex(s => s.id === serviceId)
        if (index !== -1) {
          services.value?.splice(index, 1)
        }
      }
      return { data: result, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }
  return {
    fetch,
    fetchUser,
    fetchServices,
    upsert,
    remove,
    updateOrder,
    updateServicesOrder,
    addService,
    removeService,
    normalizeData,
    userCatalogs,
    catalogs,
    services,
  }
})